/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable ban/ban */
import { Component, Prop, Ref } from '@eastsideco/salvo-ts';
import { ThemeComponent } from '@/theme/ThemeComponent';
import Plyr from 'plyr';

@Component
export class ProductGallery extends ThemeComponent {
    @Prop autoplay!: boolean;
    @Ref({ required: false }) videoPlayers!: HTMLElement[];
    @Ref({ required: false }) externalVideos!: HTMLElement[];

    private observer?: IntersectionObserver;
    private players = new Map<HTMLElement, Plyr>();

    init() {
        if (this.autoplay) {
            this.setupAutoplay();
        } else {
            this.setupHoverPlay();
        }
    }

    /**
     * Default behaviour: hosted videos play on hover and reset when the
     * cursor leaves. External embeds keep their native controls.
     */
    private setupHoverPlay() {
        if (!this.videoPlayers) { return; }
        this.videoPlayers.forEach((video) => {
            const videoPlayer = video.querySelector('video') as HTMLVideoElement | null;
            if (!videoPlayer) { return; }
            this.$listen(videoPlayer, 'mouseover', () => {
                videoPlayer.play();
            });
            this.$listen(videoPlayer, 'mouseout', () => {
                videoPlayer.pause();
                videoPlayer.currentTime = 0;
            });
        });
    }

    /**
     * Autoplay behaviour: play whichever video is currently in view and
     * pause the rest. Works on desktop (scroll) and the mobile carousel
     * (swipe) through a single IntersectionObserver.
     */
    private setupAutoplay() {
        const observer = new IntersectionObserver(
            (entries) => this.handleIntersect(entries),
            { threshold: 0.5 },
        );
        this.observer = observer;

        if (this.videoPlayers) {
            this.videoPlayers.forEach((wrapper) => {
                const video = wrapper.querySelector('video') as HTMLVideoElement | null;
                if (!video) { return; }
                video.muted = true;
                video.loop = true;
                video.playsInline = true;
                observer.observe(wrapper);
            });
        }

        if (this.externalVideos) {
            this.externalVideos.forEach((wrapper) => {
                this.players.set(wrapper, new Plyr(wrapper, {
                    muted: true,
                    controls: [],
                    loop: { active: true },
                    autoplay: false,
                }));
                observer.observe(wrapper);
            });
        }
    }

    private handleIntersect(entries: IntersectionObserverEntry[]) {
        entries.forEach((entry) => {
            const wrapper = entry.target as HTMLElement;
            const video = wrapper.querySelector('video') as HTMLVideoElement | null;
            const player = this.players.get(wrapper);

            if (entry.isIntersecting) {
                if (video) { void video.play(); }
                if (player) { player.muted = true; void player.play(); }
            } else {
                if (video) {
                    video.pause();
                    video.currentTime = 0;
                }
                if (player) { player.pause(); }
            }
        });
    }
}
